import { useEffect, useState, useRef, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LayoutGrid, Mail, Settings as SettingsIcon, LogOut, Plus, Trash2, Pencil, UploadCloud, X, ExternalLink, MessageSquare, Check, Star, DollarSign, ChevronUp, ChevronDown, Feather, MapPin, ArrowRight, Image as ImageIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { mediaUrl, formatApiErrorDetail } from "@/lib/api";
import TabErrorBoundary from "@/components/site/TabErrorBoundary";

const TYPES = ["production", "video", "inspiration", "image", "article", "post", "ad"];
const TYPE_LABELS = {
  production: "production — Productions section",
  video: "video — Voices / video grid",
  inspiration: "inspiration — Inspiration section",
  image: "image — Gallery",
  article: "article — Press / Journal clipping (external link)",
  post: "post — Article you write (Articles page)",
  ad: "ad — Spotlight / sponsor",
};
const EMPTY = { type: "production", title: "", category: "", excerpt: "", body: "", meta: "", media_id: null, thumb_id: null, external_url: "", featured: false, order: 0, subtitle: "", cta_text: "", cta_url: "", price: "", badge: "", video_id: null, embed_url: "", start_date: "", end_date: "", active: true };

function fmtDuration(sec) {
  if (!sec || !isFinite(sec)) return "";
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

// Read duration + capture a real poster frame from a video File (client-side, no server ffmpeg)
function extractVideoPoster(file) {
  return new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const v = document.createElement("video");
      v.preload = "metadata"; v.muted = true; v.playsInline = true; v.src = url;
      let done = false;
      const finish = (r) => { if (!done) { done = true; URL.revokeObjectURL(url); resolve(r); } };
      v.addEventListener("loadedmetadata", () => {
        const duration = v.duration;
        const seekTo = Math.min(1, (duration || 2) / 2);
        v.addEventListener("seeked", () => {
          try {
            const canvas = document.createElement("canvas");
            canvas.width = v.videoWidth || 1280; canvas.height = v.videoHeight || 720;
            canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
            canvas.toBlob((blob) => finish({ duration, blob }), "image/jpeg", 0.82);
          } catch { finish({ duration, blob: null }); }
        }, { once: true });
        v.currentTime = seekTo;
      });
      v.addEventListener("error", () => finish({ duration: 0, blob: null }));
      setTimeout(() => finish({ duration: v.duration || 0, blob: null }), 12000);
    } catch { resolve({ duration: 0, blob: null }); }
  });
}

function Dropzone({ label, value, onUploaded, accept, onFile }) {
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);
  const isVideo = accept && accept.includes("video");

  const upload = useCallback(async (file) => {
    if (!file) return;
    const HARD_MAX_GB = 20;
    if (file.size > HARD_MAX_GB * 1024 * 1024 * 1024) {
      toast.error(`File is ${(file.size / 1024 / 1024 / 1024).toFixed(2)}GB — max is ${HARD_MAX_GB}GB.`);
      return;
    }
    const CHUNK_THRESHOLD = 8 * 1024 * 1024; // > 8MB → chunked (small requests survive proxies/Cloudflare)
    const useChunked = file.size > CHUNK_THRESHOLD;
    // Retry transient failures (network drops, Cloudflare 5xx, 429) with backoff
    const withRetry = async (fn, label) => {
      let lastErr;
      for (let attempt = 1; attempt <= 5; attempt++) {
        try { return await fn(); }
        catch (e) {
          lastErr = e;
          const st = e.response?.status;
          const transient = !e.response || st >= 500 || st === 429 || st === 408;
          if (!transient || attempt === 5) throw e;
          toast.message(`${label} interrupted — retrying (${attempt}/4)…`, { id: "upload-progress" });
          await new Promise((res) => setTimeout(res, 1500 * attempt));
        }
      }
      throw lastErr;
    };
    setBusy(true);
    setProgress(0);
    try {
      try { await api.get("/auth/me", { timeout: 15000 }); }
      catch (e) { if (e.response?.status === 401) throw e; }
      let data;
      if (!useChunked) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/media/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 180000,
          onUploadProgress: (e) => e.total && setProgress(Math.round((e.loaded / e.total) * 100)),
        });
        data = r.data;
      } else {
        // ---- Chunked flow ----
        toast.message(`Preparing upload of ${(file.size / 1024 / 1024).toFixed(1)}MB…`, { id: "upload-progress" });
        const initResp = await withRetry(() => api.post("/media/upload/init", {
          filename: file.name, content_type: file.type || "application/octet-stream", size: file.size,
        }), "Upload");
        const sessionId = initResp.data.session_id;
        const chunkSize = initResp.data.chunk_size || 5 * 1024 * 1024;
        const total = Math.ceil(file.size / chunkSize);
        for (let i = 0; i < total; i++) {
          const blob = file.slice(i * chunkSize, Math.min((i + 1) * chunkSize, file.size));
          await withRetry(() => {
            const fd = new FormData();
            fd.append("session_id", sessionId);
            fd.append("chunk_index", String(i));
            fd.append("chunk", blob, `chunk_${i}`);
            return api.post("/media/upload/chunk", fd, {
              headers: { "Content-Type": "multipart/form-data" },
              timeout: 120000,
            });
          }, `Chunk ${i + 1}/${total}`);
          const pct = Math.round(((i + 1) / total) * 100);
          setProgress(pct);
          toast.message(`Uploading ${(file.size / 1024 / 1024).toFixed(1)}MB — ${pct}% (${i + 1}/${total} chunks)`, { id: "upload-progress" });
        }
        await withRetry(() => {
          const finFd = new FormData();
          finFd.append("session_id", sessionId);
          return api.post("/media/upload/finalize", finFd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 60000,
          });
        }, "Finalize");
        // Poll for background assembly to complete (avoids Cloudflare 100s timeout on big files)
        toast.message("Processing upload on server…", { id: "upload-progress" });
        const started = Date.now();
        while (true) {
          if (Date.now() - started > 30 * 60 * 1000) throw new Error("Server processing timed out.");
          await new Promise((res) => setTimeout(res, 2500));
          const st = await withRetry(() => api.get(`/media/upload/status/${sessionId}`, { timeout: 30000 }), "Status check");
          if (st.data.status === "done") { data = st.data; break; }
          if (st.data.status === "error") throw new Error(st.data.error || "Server failed to assemble file.");
          if (st.data.expected > 0) {
            const p = Math.round((st.data.assembled / st.data.expected) * 100);
            toast.message(`Processing on server — ${p}%`, { id: "upload-progress" });
          }
        }
        toast.dismiss("upload-progress");
      }
      onUploaded(data.id);
      const mb = data.size / 1024 / 1024;
      const sizeLabel = mb < 1 ? `${(data.size / 1024).toFixed(0)}KB` : mb < 1024 ? `${mb.toFixed(2)}MB` : `${(mb / 1024).toFixed(2)}GB`;
      toast.success(`Uploaded ${sizeLabel}`);
      if (onFile) onFile(file);
    } catch (e) {
      toast.dismiss("upload-progress");
      const status = e.response?.status;
      const raw = e.response?.data;
      const detail = raw && typeof raw === "object" ? raw.detail : null;
      let msg = "Upload failed";
      if (status === 413) msg = detail || "File too large.";
      else if (status === 401) msg = "Session expired — please log out and back in, then retry the upload.";
      else if (status >= 500 || status === 408) msg = `Connection to the server was interrupted (error ${status}) after several retries. Please check your connection and try again.`;
      else if (detail) msg = `Upload failed: ${formatApiErrorDetail(detail)}`;
      else if (e.code === "ECONNABORTED") msg = "Upload timed out. Check your connection and retry.";
      else if (e.message) msg = `Upload failed: ${e.message}`;
      toast.error(msg, { duration: 8000 });
    }
    finally { setBusy(false); setProgress(0); }
  }, [onUploaded, onFile]);

  const clear = useCallback(async (e) => {
    e.stopPropagation();
    if (!value) return;
    const oldId = value;
    onUploaded(null);
    try {
      await api.delete(`/media/${oldId}`);
      toast.success("Removed");
    } catch { /* silent — the value is already cleared client-side */ }
  }, [value, onUploaded]);

  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-2">{label}</label>
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files[0]); }}
        onClick={() => inputRef.current?.click()}
        className={`relative cursor-pointer border border-dashed ${drag ? "border-imi-gold bg-imi-gold/5" : "border-imi-text/30"} bg-imi-elevated p-4 flex items-center gap-4 transition-colors`}
        data-testid={`drop-${label.toLowerCase().replace(/\s/g, "-")}`}
      >
        {value ? (
          isVideo ? (
            <div className="w-16 h-16 flex items-center justify-center bg-imi-bg border border-imi-gold/40 text-imi-gold text-[10px] uppercase tracking-wider">Video</div>
          ) : (
            <img src={mediaUrl({ media_id: value }) || undefined} alt="" className="w-16 h-16 object-cover border border-imi-border" />
          )
        ) : (
          <div className="w-16 h-16 flex items-center justify-center bg-imi-bg border border-imi-border"><UploadCloud size={22} className="text-imi-text/40" /></div>
        )}
        <div className="text-sm text-imi-text/60 flex-1">
          {busy ? (progress > 0 ? `Uploading… ${progress}%` : "Uploading…") : value ? "Uploaded — click to replace" : "Drag & drop or click to upload"}
          {busy && progress > 0 && (
            <div className="mt-2 h-1 bg-imi-border overflow-hidden">
              <div className="h-full bg-imi-gold transition-all" style={{ width: `${progress}%` }} data-testid={`upload-progress-${label.toLowerCase().replace(/\s/g, "-")}`} />
            </div>
          )}
        </div>
        {value && !busy && (
          <button
            type="button"
            onClick={clear}
            data-testid={`clear-${label.toLowerCase().replace(/\s/g, "-")}`}
            title="Remove uploaded file"
            className="p-1.5 text-imi-text/40 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
          >
            <X size={16} />
          </button>
        )}
        <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={(e) => upload(e.target.files[0])} />
      </div>
      {isVideo && (
        <p className="text-imi-text/40 text-[11px] mt-1.5">
          Large files auto-chunk — up to 20GB supported. For faster uploads, compress at{" "}
          <a href="https://www.freeconvert.com/video-compressor" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-imi-gold hover:underline">freeconvert.com/video-compressor</a>.
        </p>
      )}
    </div>
  );
}

function PullImagesButton({ onClick, busy }) {
  return (
    <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
      <p className="text-imi-text/40 text-[11px]">No pictures came through? Fill in the article link above, then:</p>
      <button type="button" onClick={onClick} disabled={busy} data-testid="pull-images-btn"
        className="inline-flex items-center gap-2 border border-imi-gold/50 text-imi-gold px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] hover:bg-imi-gold/10 disabled:opacity-60">
        <ImageIcon size={12} /> {busy ? "Fetching…" : "Pull images from the article link"}
      </button>
    </div>
  );
}

function ContentEditor({ item, onClose, onSaved }) {
  const [form, setForm] = useState(item || EMPTY);
  const [saving, setSaving] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.type === "checkbox" ? e.target.checked : e.target.value }));

  // Rich paste: keep the images that come along with copied article text as ![caption](url) lines
  const onBodyPaste = (e) => {
    const html = e.clipboardData?.getData("text/html");
    if (!html || !/<img/i.test(html)) return;
    e.preventDefault();
    const doc = new DOMParser().parseFromString(html, "text/html");
    doc.querySelectorAll("script,style,noscript,svg,button,nav,header,footer,aside").forEach((n) => n.remove());
    const baseUrl = form.cta_url || importUrl || "";
    const imgs = [];
    doc.querySelectorAll("img").forEach((img) => {
      let src = img.getAttribute("data-src") || img.getAttribute("data-lazy-src") || img.getAttribute("src") || "";
      if ((!src || src.startsWith("data:")) && img.getAttribute("srcset")) src = img.getAttribute("srcset").split(",").pop().trim().split(/\s+/)[0];
      if (src && !/^https?:\/\//i.test(src) && baseUrl) { try { src = new URL(src, baseUrl).href; } catch { /* keep */ } }
      const w = parseInt(img.getAttribute("width") || "0", 10);
      if (!/^https?:\/\//i.test(src) || (w && w < 80)) { img.remove(); return; }
      const cap = (img.getAttribute("alt") || "").replace(/[\[\]()]/g, "").trim().slice(0, 140);
      imgs.push(src);
      img.replaceWith(doc.createTextNode(`\n\n![${cap}](${src})\n\n`));
    });
    doc.querySelectorAll("p,div,h1,h2,h3,h4,li,blockquote,figure,figcaption,br,tr").forEach((n) => {
      n.before(doc.createTextNode("\n")); n.after(doc.createTextNode("\n"));
    });
    const text = (doc.body.textContent || "").replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
    const ta = e.target;
    const start = ta.selectionStart ?? ta.value.length, end = ta.selectionEnd ?? ta.value.length;
    const next = ta.value.slice(0, start) + text + ta.value.slice(end);
    setForm((f) => ({
      ...f, body: next,
      // Use the first pasted image as the preview if none is set yet
      external_url: f.media_id || f.thumb_id || f.external_url ? f.external_url : (imgs[0] || f.external_url),
    }));
    if (imgs.length) toast.success(`Pasted text with ${imgs.length} image${imgs.length > 1 ? "s" : ""}.`);
  };

  // Fallback when the paste carried no images: fetch the article page and append its photos
  const [pulling, setPulling] = useState(false);
  const pullImages = async () => {
    const url = (form.cta_url || importUrl || "").trim();
    if (!url) { toast.error("Add the article link first (Read the full article — link)."); return; }
    setPulling(true);
    try {
      const { data } = await api.post("/admin/fetch-url", { url });
      const fresh = (data.images || []).filter((im) => !(form.body || "").includes(im.url));
      if (!fresh.length) { toast.message(data.images?.length ? "All of this article's images are already in the body." : "No usable images found on that page."); return; }
      const lines = fresh.map((im) => `![${(im.alt || "").replace(/[\[\]()]/g, "")}](${im.url})`).join("\n\n");
      setForm((f) => ({
        ...f,
        body: `${(f.body || "").trim()}\n\n${lines}`.trim(),
        external_url: f.media_id || f.thumb_id || f.external_url ? f.external_url : fresh[0].url,
      }));
      toast.success(`Added ${fresh.length} image${fresh.length > 1 ? "s" : ""} from the article. Drag the ![…] lines to where they belong.`);
    } catch { toast.error("Could not fetch that page."); }
    finally { setPulling(false); }
  };

  // One-click import: fetch OG/Twitter tags from any URL (Washington Post, etc.) and
  // populate title/excerpt/thumbnail so the admin doesn't have to type any of it.
  const runImport = async () => {
    const url = importUrl.trim();
    if (!url) { toast.error("Paste an article URL first."); return; }
    setImporting(true);
    try {
      const { data } = await api.post("/admin/fetch-url", { url });
      const updates = {};
      const isYouTube = data.provider === "youtube";
      if (data.title && !form.title) updates.title = data.title;
      else if (data.title) updates.title = data.title;
      if (data.excerpt) updates.excerpt = data.excerpt;
      // For YouTube: set embed_url so the player renders; keep external link too.
      // For articles: cta_url is the article page.
      if (isYouTube) {
        updates.embed_url = data.embed_url || data.url;
        updates.cta_url = data.url;
        // If admin hasn't picked a category, default to podcast for YouTube content.
        updates.category = form.category || "podcast";
        // For YouTube, also make sure type is production so it flows into the Podcast tab.
        if (!form.type || form.type === "article") updates.type = "production";
      } else {
        updates.cta_url = data.url;
        updates.category = form.category || (data.site_name ? data.site_name.toLowerCase() : "article");
      }
      // Try to fetch and upload the preview image to our media store so it persists
      if (data.image_url) {
        try {
          const resp = await fetch(data.image_url, { mode: "cors" }).catch(() => null);
          if (resp && resp.ok) {
            const blob = await resp.blob();
            const fd = new FormData();
            fd.append("file", new File([blob], "og-image.jpg", { type: blob.type || "image/jpeg" }));
            const { data: mu } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            updates.media_id = mu.id;
          } else {
            // Fallback: store the direct URL so at least something displays
            updates.external_url = data.image_url;
          }
        } catch {
          updates.external_url = data.image_url;
        }
      }
      setForm((f) => ({ ...f, ...updates }));
      toast.success(`Imported from ${data.site_name || "URL"} — review and click Save.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not fetch the URL. You can still fill fields manually.");
    } finally {
      setImporting(false);
    }
  };

  // Auto-fill real duration + capture a poster frame when a video file is uploaded
  const handleVideoFile = async (file) => {
    if (!file || !file.type?.startsWith("video/")) return;
    toast.message("Reading video — grabbing duration & poster…");
    const { duration, blob } = await extractVideoPoster(file);
    const updates = {};
    if (duration) updates.meta = fmtDuration(duration);
    if (blob) {
      try {
        const fd = new FormData();
        fd.append("file", new File([blob], "poster.jpg", { type: "image/jpeg" }));
        const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        updates.thumb_id = data.id;
      } catch { /* ignore poster failure */ }
    }
    if (Object.keys(updates).length) {
      setForm((f) => ({ ...f, ...updates }));
      toast.success("Auto-filled duration & poster frame");
    }
  };

  const save = async () => {
    if (!form.title) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      if (form.id) await api.put(`/content/${form.id}`, form);
      else await api.post("/content", form);
      toast.success("Saved");
      onSaved();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-imi-bg/80 backdrop-blur flex justify-end" data-testid="content-editor">
      <div className="w-full max-w-xl h-full bg-imi-surface border-l border-imi-border overflow-y-auto">
        <div className="sticky top-0 bg-imi-surface border-b border-imi-border px-6 py-4 flex justify-between items-center z-10">
          <h3 className="font-serif text-xl text-imi-text">{form.id ? "Edit" : "New"} item</h3>
          <button onClick={onClose} data-testid="editor-close"><X className="text-imi-text/60 hover:text-imi-gold" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-2">Type</label>
              <select value={form.type} onChange={set("type")} data-testid="editor-type" className="w-full bg-imi-bg border border-imi-border px-3 py-2.5 text-imi-text outline-none focus:border-imi-gold">
                {TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>)}
              </select>
              {(form.type === "article" || form.type === "post") && (
                <p className="mt-1.5 text-imi-text/45 text-[11px] leading-snug" data-testid="type-hint">
                  {form.type === "post"
                    ? "Shows on the Articles page and the home-page Articles preview."
                    : "Shows in the home-page Journal and on the Articles page (opens the source link)."}
                </p>
              )}
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-2">Category</label>
              <select value={form.category || ""} onChange={set("category")} data-testid="editor-category" className="w-full bg-imi-bg border border-imi-border px-3 py-2.5 text-imi-text outline-none focus:border-imi-gold">
                <option value="">— select —</option>
                <option value="podcast">Podcast</option>
                <option value="documentary">Documentary</option>
                <option value="investigation">Investigation</option>
                <option value="article">Article</option>
                <option value="opinion">Opinion</option>
                <option value="news">News</option>
                <option value="advocacy">Advocacy</option>
                <option value="reflection">Reflection</option>
                <option value="poetry">Poetry</option>
                <option value="music">Music</option>
                <option value="spoken-word">Spoken Word</option>
                <option value="other">Other</option>
              </select>
              <div className="text-imi-text/40 text-[10px] mt-1">Determines which tab the item shows under on the homepage. Must be lowercase (dropdown enforces this).</div>
            </div>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-2">Title</label>
            <input value={form.title} onChange={set("title")} data-testid="editor-title" className="w-full bg-imi-bg border border-imi-border px-3 py-2.5 text-imi-text outline-none focus:border-imi-gold" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-2">Excerpt</label>
            <textarea rows={2} value={form.excerpt} onChange={set("excerpt")} data-testid="editor-excerpt" className="w-full bg-imi-bg border border-imi-border px-3 py-2.5 text-imi-text outline-none focus:border-imi-gold resize-none" />
          </div>
          {(form.type === "article" || form.type === "production") && (
            <>
              <div className="border border-imi-gold/30 bg-imi-gold/5 p-4 space-y-3" data-testid="article-import">
                <div className="text-[11px] uppercase tracking-[0.25em] text-imi-gold font-semibold">
                  {form.type === "production" ? "One-click YouTube / article import" : "One-click article import"}
                </div>
                <div className="text-imi-text/50 text-xs">
                  {form.type === "production"
                    ? "Paste a YouTube URL (or article link) and we'll auto-fill the title, thumbnail, embed URL, and set category to podcast."
                    : "Paste a URL (Washington Post, NYT, etc.) and we'll auto-fill the title, excerpt and preview image below."}
                </div>
                <div className="flex gap-2">
                  <input
                    value={importUrl}
                    onChange={(e) => setImportUrl(e.target.value)}
                    placeholder={form.type === "production" ? "https://youtu.be/… or article URL" : "https://www.washingtonpost.com/…"}
                    data-testid="editor-import-url"
                    className="flex-1 bg-imi-bg border border-imi-border px-3 py-2.5 text-imi-text placeholder:text-imi-text/40 outline-none focus:border-imi-gold"
                  />
                  <button
                    type="button"
                    onClick={runImport}
                    disabled={importing || !importUrl.trim()}
                    data-testid="editor-import-btn"
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-imi-gold text-imi-bg text-[11px] uppercase tracking-[0.15em] font-semibold hover:bg-imi-goldlight disabled:opacity-60"
                  >
                    {importing ? "Fetching…" : "Import"} <ArrowRight size={14} />
                  </button>
                </div>
              </div>
              {form.type === "article" && (
              <>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-2">Read the full article — link (opens in new tab)</label>
                <input
                  value={form.cta_url || ""}
                  onChange={set("cta_url")}
                  data-testid="editor-article-url"
                  placeholder="https://www.washingtonpost.com/…"
                  className="w-full bg-imi-bg border border-imi-border px-3 py-2.5 text-imi-text placeholder:text-imi-text/40 outline-none focus:border-imi-gold"
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-2">Body — paste article text here (optional)</label>
                <textarea rows={12} value={form.body} onChange={set("body")} onPaste={onBodyPaste} data-testid="editor-body"
                  placeholder="Paste the article's text, excerpts, or your own commentary here. Line breaks are preserved. Images copied with the text are kept automatically."
                  className="w-full bg-imi-bg border border-imi-border px-3 py-2.5 text-imi-text placeholder:text-imi-text/40 outline-none focus:border-imi-gold resize-y" />
                <PullImagesButton onClick={pullImages} busy={pulling} />
              </div>
              </>
              )}
            </>
          )}
          {form.type === "post" && (
            <div className="border border-imi-gold/30 bg-imi-gold/5 p-4 space-y-4" data-testid="post-writer">
              <div className="text-[11px] uppercase tracking-[0.25em] text-imi-gold font-semibold">Original article — write &amp; publish</div>
              <div className="text-imi-text/50 text-xs">
                This publishes to the <span className="text-imi-gold">/articles</span> page (and the homepage preview). Blank line = new paragraph.
                Start a line with <span className="text-imi-gold">##&nbsp;</span> for a section heading, or <span className="text-imi-gold">&gt;&nbsp;</span> for a pull-quote.
                Images copied along with pasted text are kept as <span className="text-imi-gold">![caption](url)</span> lines and shown in the article. Add a cover image via the Image upload below. Tick <span className="text-imi-gold">Featured</span> to pin it in the top slider.
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-2">Article text</label>
                <textarea rows={18} value={form.body} onChange={set("body")} onPaste={onBodyPaste} data-testid="editor-post-body"
                  placeholder={"Write your article here…\n\n## A section heading\n\nAnother paragraph.\n\n> A quote that deserves to stand out."}
                  className="w-full bg-imi-bg border border-imi-border px-3 py-2.5 text-imi-text placeholder:text-imi-text/40 outline-none focus:border-imi-gold resize-y" />
                <PullImagesButton onClick={pullImages} busy={pulling} />
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-imi-text/70">
                <input type="checkbox" checked={!!form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} data-testid="editor-post-featured" className="accent-imi-gold" />
                Featured (pinned in the top slider on /articles)
              </label>
            </div>
          )}
          {form.type === "ad" && (
            <>
              <div className="border border-imi-gold/30 bg-imi-gold/5 p-4 space-y-4">
                <div className="text-[11px] uppercase tracking-[0.25em] text-imi-gold font-semibold">Ad / Spotlight settings</div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-2">Subtitle / Kicker</label>
                    <input value={form.subtitle || ""} onChange={set("subtitle")} data-testid="editor-subtitle" placeholder="New release" className="w-full bg-imi-bg border border-imi-border px-3 py-2.5 text-imi-text outline-none focus:border-imi-gold" />
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-2">Badge</label>
                    <input value={form.badge || ""} onChange={set("badge")} data-testid="editor-badge" placeholder="Bestseller" className="w-full bg-imi-bg border border-imi-border px-3 py-2.5 text-imi-text outline-none focus:border-imi-gold" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-2">Price / tag</label>
                    <input value={form.price || ""} onChange={set("price")} data-testid="editor-price" placeholder="$19.99" className="w-full bg-imi-bg border border-imi-border px-3 py-2.5 text-imi-text outline-none focus:border-imi-gold" />
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-2">CTA button label</label>
                    <input value={form.cta_text || ""} onChange={set("cta_text")} data-testid="editor-cta-text" placeholder="Order the book" className="w-full bg-imi-bg border border-imi-border px-3 py-2.5 text-imi-text outline-none focus:border-imi-gold" />
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-2">CTA button URL</label>
                  <input value={form.cta_url || ""} onChange={set("cta_url")} data-testid="editor-cta-url" placeholder="https://amazon.com/…" className="w-full bg-imi-bg border border-imi-border px-3 py-2.5 text-imi-text outline-none focus:border-imi-gold" />
                </div>

                <Dropzone label="Video (autoplays muted, optional)" value={form.video_id} accept="video/*"
                  onUploaded={(id) => setForm((f) => ({ ...f, video_id: id }))} />

                <div>
                  <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-2">…or YouTube / Vimeo URL</label>
                  <input value={form.embed_url || ""} onChange={set("embed_url")} data-testid="editor-embed" placeholder="https://youtu.be/…" className="w-full bg-imi-bg border border-imi-border px-3 py-2.5 text-imi-text outline-none focus:border-imi-gold" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-2">Schedule — start</label>
                    <input type="date" value={form.start_date || ""} onChange={set("start_date")} data-testid="editor-start-date" className="w-full bg-imi-bg border border-imi-border px-3 py-2.5 text-imi-text outline-none focus:border-imi-gold" />
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-2">Schedule — end</label>
                    <input type="date" value={form.end_date || ""} onChange={set("end_date")} data-testid="editor-end-date" className="w-full bg-imi-bg border border-imi-border px-3 py-2.5 text-imi-text outline-none focus:border-imi-gold" />
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <label className="inline-flex items-center gap-2 text-sm text-imi-text/70" data-testid="editor-active-wrap">
                    <input type="checkbox" checked={form.active !== false} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} data-testid="editor-active" className="accent-imi-gold" />
                    Active
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm text-imi-text/70">
                    <input type="checkbox" checked={!!form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} data-testid="editor-featured" className="accent-imi-gold" />
                    Featured (top spotlight)
                  </label>
                </div>
              </div>
            </>
          )}
          <div className="grid grid-cols-2 gap-4">
            {form.type !== "ad" && (
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-2">Meta (e.g. 42 min)</label>
                <input value={form.meta} onChange={set("meta")} data-testid="editor-meta" className="w-full bg-imi-bg border border-imi-border px-3 py-2.5 text-imi-text outline-none focus:border-imi-gold" />
              </div>
            )}
            <div className={form.type === "ad" ? "col-span-2" : ""}>
              <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-2">Order</label>
              <input type="number" value={form.order} onChange={set("order")} data-testid="editor-order" className="w-full bg-imi-bg border border-imi-border px-3 py-2.5 text-imi-text outline-none focus:border-imi-gold" />
            </div>
          </div>

          {(form.type === "video" || form.type === "inspiration") && (
            <Dropzone label="Video file" value={form.media_id} accept="video/*" onFile={handleVideoFile} onUploaded={(id) => setForm((f) => ({ ...f, media_id: id }))} />
          )}
          <Dropzone label={(form.type === "video" || form.type === "inspiration") ? "Thumbnail" : "Image"} value={(form.type === "video" || form.type === "inspiration") ? form.thumb_id : form.media_id} accept="image/*"
            onUploaded={(id) => setForm((f) => ((f.type === "video" || f.type === "inspiration") ? { ...f, thumb_id: id } : { ...f, media_id: id }))} />
          <div>
            <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-2">…or external image URL</label>
            <input value={form.external_url} onChange={set("external_url")} data-testid="editor-external" placeholder="https://…" className="w-full bg-imi-bg border border-imi-border px-3 py-2.5 text-imi-text outline-none focus:border-imi-gold" />
          </div>

          {/* Optional extra media — any content type (except ad, which has its own richer block) can attach a YouTube/Vimeo URL and/or an uploaded video */}
          {form.type !== "ad" && (
            <div className="border border-imi-border bg-imi-elevated/50 p-4 space-y-4">
              <div className="text-[11px] uppercase tracking-[0.25em] text-imi-text/50 font-semibold">
                {(form.type === "video" || form.type === "inspiration") ? "Alternative source (YouTube / Vimeo)" : "Optional video / trailer"}
              </div>
              {form.type !== "video" && form.type !== "inspiration" && (
                <Dropzone label="Video (autoplays in viewer)" value={form.video_id} accept="video/*"
                  onUploaded={(id) => setForm((f) => ({ ...f, video_id: id }))} />
              )}
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-2">
                  {(form.type === "video" || form.type === "inspiration") ? "YouTube / Vimeo URL (use instead of uploading)" : "…or YouTube / Vimeo URL"}
                </label>
                <input value={form.embed_url || ""} onChange={set("embed_url")} data-testid="editor-embed-optional" placeholder="https://youtu.be/…" className="w-full bg-imi-bg border border-imi-border px-3 py-2.5 text-imi-text outline-none focus:border-imi-gold" />
              </div>
              <p className="text-imi-text/40 text-[11px]">
                {(form.type === "video" || form.type === "inspiration")
                  ? "Paste a YouTube or Vimeo link to skip uploading. The piece will play in the lightbox."
                  : "Visitors will see this play in the pop-up viewer when they open the item. Combine with the image above for a rich preview + trailer + long-form body."}
              </p>
            </div>
          )}

          <button onClick={save} disabled={saving} data-testid="editor-save" className="w-full bg-imi-gold text-imi-bg py-3 text-sm uppercase tracking-[0.15em] font-semibold hover:bg-imi-goldlight transition-colors disabled:opacity-60">
            {saving ? "Saving…" : "Save item"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ContentTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/content").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/content/${id}`);
    toast.success("Deleted"); load();
  };

  const shown = filter === "all" ? items : items.filter((i) => i.type === filter);

  // Swap `order` values with the previous/next item of the same type
  const move = async (id, dir) => {
    const it = items.find((x) => x.id === id);
    if (!it) return;
    const siblings = items.filter((x) => x.type === it.type).sort((a, b) => (a.order || 0) - (b.order || 0));
    const idx = siblings.findIndex((x) => x.id === id);
    const swap = siblings[idx + dir];
    if (!swap) return;
    const a = it.order || 0;
    const b = swap.order || 0;
    // Ensure the swapped values are actually different, otherwise nudge
    const newA = a === b ? b + dir : b;
    const newB = a === b ? a : a;
    try {
      await Promise.all([
        api.put(`/content/${it.id}`, { order: newA }),
        api.put(`/content/${swap.id}`, { order: newB }),
      ]);
      load();
    } catch { toast.error("Reorder failed"); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex gap-2">
          {["all", ...TYPES].map((t) => (
            <button key={t} onClick={() => setFilter(t)} data-testid={`tab-filter-${t}`}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] border ${filter === t ? "border-imi-gold text-imi-gold" : "border-imi-border text-imi-text/50"}`}>{t}</button>
          ))}
        </div>
        <button onClick={() => setEditing(EMPTY)} data-testid="new-content-btn" className="inline-flex items-center gap-2 bg-imi-gold text-imi-bg px-4 py-2 text-[12px] uppercase tracking-[0.15em] font-semibold hover:bg-imi-goldlight transition-colors">
          <Plus size={16} /> New
        </button>
      </div>
      <div className="border border-imi-border">
        {shown.map((it, i) => {
          const siblings = items.filter((x) => x.type === it.type).sort((a, b) => (a.order || 0) - (b.order || 0));
          const pos = siblings.findIndex((x) => x.id === it.id);
          const isFirst = pos <= 0;
          const isLast = pos === siblings.length - 1;
          const isAd = it.type === "ad";
          const scheduled = isAd && (it.start_date || it.end_date);
          const inactive = isAd && it.active === false;
          return (
            <div key={it.id} data-testid={`row-${it.id}`} className="flex items-center gap-3 px-4 py-3 border-b border-imi-border last:border-b-0 hover:bg-imi-elevated transition-colors">
              <div className="flex flex-col">
                <button onClick={() => move(it.id, -1)} disabled={isFirst} data-testid={`move-up-${it.id}`}
                  className="p-0.5 text-imi-text/40 hover:text-imi-gold disabled:opacity-20 disabled:cursor-not-allowed"><ChevronUp size={14} /></button>
                <button onClick={() => move(it.id, 1)} disabled={isLast} data-testid={`move-down-${it.id}`}
                  className="p-0.5 text-imi-text/40 hover:text-imi-gold disabled:opacity-20 disabled:cursor-not-allowed"><ChevronDown size={14} /></button>
              </div>
              {mediaUrl(it) ? (
                <img src={mediaUrl(it)} alt="" className="w-14 h-14 object-cover border border-imi-border bg-imi-elevated" onError={(e) => { e.target.style.visibility = "hidden"; }} />
              ) : (
                <div className="w-14 h-14 border border-imi-border bg-imi-elevated flex items-center justify-center"><UploadCloud size={16} className="text-imi-text/30" /></div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-imi-text truncate">{it.title}</div>
                  {it.featured && <Star size={12} className="text-imi-gold fill-imi-gold" />}
                  {inactive && <span className="text-[9px] uppercase tracking-wider text-red-400 border border-red-500/40 px-1.5 py-0.5">Inactive</span>}
                  {scheduled && !inactive && <span className="text-[9px] uppercase tracking-wider text-imi-gold border border-imi-gold/40 px-1.5 py-0.5" title={`${it.start_date || "…"} → ${it.end_date || "…"}`}>Scheduled</span>}
                </div>
                <div className="text-imi-text/40 text-xs uppercase tracking-wider">{it.type} · {it.category} · #{it.order ?? 0}</div>
              </div>
              <button onClick={() => setEditing(it)} data-testid={`edit-${it.id}`} className="p-2 text-imi-text/50 hover:text-imi-gold"><Pencil size={16} /></button>
              <button onClick={() => del(it.id)} data-testid={`delete-${it.id}`} className="p-2 text-imi-text/50 hover:text-red-400"><Trash2 size={16} /></button>
            </div>
          );
        })}
        {shown.length === 0 && <div className="p-8 text-center text-imi-text/40">No items yet.</div>}
      </div>
      {editing && <ContentEditor item={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function MessagesTab() {
  const [msgs, setMsgs] = useState([]);
  const load = () => api.get("/contact").then((r) => setMsgs(r.data));
  useEffect(() => { load(); }, []);
  const del = async (id) => { await api.delete(`/contact/${id}`); load(); };
  return (
    <div className="space-y-4">
      {msgs.map((m) => (
        <div key={m.id} data-testid={`msg-${m.id}`} className="border border-imi-border p-5 bg-imi-elevated">
          <div className="flex justify-between items-start gap-4">
            <div>
              <div className="text-imi-text font-medium">{m.name} <span className="text-imi-text/40 text-sm">· {m.email}</span></div>
              <div className="text-imi-gold text-sm mt-0.5">{m.subject}</div>
            </div>
            <button onClick={() => del(m.id)} className="text-imi-text/40 hover:text-red-400"><Trash2 size={16} /></button>
          </div>
          <p className="text-imi-text/70 mt-3 text-sm leading-relaxed">{m.message}</p>
          <div className="text-imi-text/30 text-xs mt-3">{new Date(m.created_at).toLocaleString()}</div>
        </div>
      ))}
      {msgs.length === 0 && <div className="p-8 text-center text-imi-text/40 border border-imi-border">No messages yet.</div>}
    </div>
  );
}

function StoriesTab() {
  const [stories, setStories] = useState([]);
  const [filter, setFilter] = useState("pending");
  const load = () => api.get("/stories").then((r) => setStories(r.data));
  useEffect(() => { load(); }, []);

  const act = async (id, patch) => {
    try { await api.put(`/stories/${id}`, patch); toast.success("Updated"); load(); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this story permanently?")) return;
    await api.delete(`/stories/${id}`); toast.success("Deleted"); load();
  };

  const STATUSES = ["pending", "approved", "rejected", "all"];
  const shown = filter === "all" ? stories : stories.filter((s) => s.status === filter);
  const counts = {
    pending: stories.filter((s) => s.status === "pending").length,
    approved: stories.filter((s) => s.status === "approved").length,
    rejected: stories.filter((s) => s.status === "rejected").length,
  };

  const badgeColor = (st) => st === "approved" ? "text-green-400 border-green-500/40"
    : st === "rejected" ? "text-red-400 border-red-500/40" : "text-imi-gold border-imi-gold/40";

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-6">
        {STATUSES.map((st) => (
          <button key={st} onClick={() => setFilter(st)} data-testid={`story-filter-${st}`}
            className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] border ${filter === st ? "border-imi-gold text-imi-gold" : "border-imi-border text-imi-text/50"}`}>
            {st}{st !== "all" && counts[st] > 0 ? ` (${counts[st]})` : ""}
          </button>
        ))}
      </div>
      <div className="space-y-4">
        {shown.map((s) => {
          const media = s.media_id ? mediaUrl({ media_id: s.media_id }) : null;
          return (
            <div key={s.id} data-testid={`story-${s.id}`} className="border border-imi-border p-5 bg-imi-elevated">
              <div className="flex flex-wrap justify-between items-start gap-3">
                <div>
                  <div className="flex items-center gap-3">
                    <span className="font-serif text-lg text-imi-text">{s.alias}</span>
                    <span className={`text-[10px] uppercase tracking-[0.15em] border px-2 py-0.5 ${badgeColor(s.status)}`}>{s.status}</span>
                    <span className="text-[10px] uppercase tracking-[0.15em] text-imi-text/40 border border-imi-border px-2 py-0.5">{s.format}</span>
                    {s.featured && <Star size={14} className="text-imi-gold fill-imi-gold" />}
                  </div>
                  <div className="text-imi-text/40 text-xs mt-1">{s.contact} · {new Date(s.created_at).toLocaleString()}</div>
                </div>
                <div className="flex items-center gap-2">
                  {s.status !== "approved" && (
                    <button onClick={() => act(s.id, { status: "approved" })} data-testid={`approve-${s.id}`}
                      className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider bg-green-600/20 text-green-400 border border-green-500/40 px-3 py-1.5 hover:bg-green-600/30 transition-colors">
                      <Check size={14} /> Approve
                    </button>
                  )}
                  {s.status !== "rejected" && (
                    <button onClick={() => act(s.id, { status: "rejected" })} data-testid={`reject-${s.id}`}
                      className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider bg-red-600/10 text-red-400 border border-red-500/40 px-3 py-1.5 hover:bg-red-600/20 transition-colors">
                      <X size={14} /> Reject
                    </button>
                  )}
                  <button onClick={() => act(s.id, { featured: !s.featured })} title="Feature"
                    className={`p-2 border border-imi-border ${s.featured ? "text-imi-gold" : "text-imi-text/40 hover:text-imi-gold"}`}><Star size={14} /></button>
                  <button onClick={() => del(s.id)} className="p-2 text-imi-text/40 hover:text-red-400"><Trash2 size={14} /></button>
                </div>
              </div>
              {media && s.media_type?.startsWith("audio") && <audio controls src={media} className="w-full mt-4" />}
              {media && s.media_type?.startsWith("video") && <video controls src={media} preload="metadata" className="w-full max-w-md mt-4 border border-imi-border" />}
              {s.story && <p className="text-imi-text/70 mt-4 text-sm leading-relaxed whitespace-pre-line">{s.story}</p>}
            </div>
          );
        })}
        {shown.length === 0 && <div className="p-8 text-center text-imi-text/40 border border-imi-border">No {filter !== "all" ? filter : ""} stories.</div>}
      </div>
    </div>
  );
}

function SupportTab() {
  const [donations, setDonations] = useState([]);
  const [subs, setSubs] = useState([]);
  const [screenings, setScreenings] = useState([]);
  const [stories, setStories] = useState([]);
  const [sending, setSending] = useState({});
  const load = () => {
    api.get("/payments").then((r) => setDonations(r.data)).catch(() => {});
    api.get("/newsletter").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/screening-request").then((r) => setScreenings(r.data)).catch(() => {});
    api.get("/family-story").then((r) => setStories(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const paid = donations.filter((d) => d.payment_status === "paid");
  const total = paid.reduce((sum, d) => sum + (d.amount || 0), 0);
  const thanked = paid.filter((d) => d.thank_you_sent_at).length;
  const delSub = async (id) => { await api.delete(`/newsletter/${id}`); load(); };
  const delScreening = async (id) => { await api.delete(`/screening-request/${id}`); load(); };
  const delStory = async (id) => { if (!window.confirm("Delete this family story submission?")) return; await api.delete(`/family-story/${id}`); load(); };
  const setStoryStatus = async (id, status) => { await api.patch(`/family-story/${id}`, { status }); load(); };
  const promoteStory = async (id, name) => {
    if (!window.confirm(`Promote ${name} to the public "Still Inside" grid?`)) return;
    try {
      const { data } = await api.post(`/family-story/${id}/promote`);
      alert(`✓ ${data.name} added to Still Inside. Now editable at Settings → Still Inside.`);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || "Unable to promote — please check consent and verification status.");
    }
  };
  const resendThanks = async (session_id) => {
    setSending((m) => ({ ...m, [session_id]: true }));
    try {
      await api.post(`/payments/${session_id}/resend-thanks`);
      toast.success("Thank-you email sent.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send thank-you.");
    } finally {
      setSending((m) => ({ ...m, [session_id]: false }));
    }
  };
  const copy = (text) => { navigator.clipboard.writeText(text || ""); toast.success("Copied"); };

  return (
    <div className="space-y-10">
      <div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-imi-border border border-imi-border mb-6">
          <div className="bg-imi-bg p-6"><div className="font-serif text-4xl text-imi-goldlight" data-testid="support-total">${total.toFixed(0)}</div><div className="text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mt-2">Total raised</div></div>
          <div className="bg-imi-bg p-6"><div className="font-serif text-4xl text-imi-goldlight" data-testid="support-count">{paid.length}</div><div className="text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mt-2">Donations</div></div>
          <div className="bg-imi-bg p-6"><div className="font-serif text-4xl text-imi-goldlight" data-testid="support-thanked">{thanked}/{paid.length}</div><div className="text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mt-2">Thank-yous sent</div></div>
          <div className="bg-imi-bg p-6"><div className="font-serif text-4xl text-imi-goldlight">{subs.length}</div><div className="text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mt-2">Subscribers</div></div>
        </div>

        <div className="flex items-baseline justify-between mb-4">
          <h3 className="font-serif text-xl text-imi-goldlight">Donations</h3>
          <div className="text-imi-text/40 text-xs">Newest first · click any email to copy</div>
        </div>

        <div className="border border-imi-border">
          {paid.map((d) => {
            const isSending = !!sending[d.session_id];
            const wasSent = !!d.thank_you_sent_at;
            return (
              <div key={d.session_id} data-testid={`donation-${d.session_id}`} className="flex flex-col md:flex-row md:items-center gap-3 md:gap-5 px-4 py-4 border-b border-imi-border last:border-b-0 hover:bg-imi-elevated transition-colors">
                <span className="font-serif text-2xl text-imi-goldlight md:w-24">${d.amount}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-imi-text truncate">
                    <span className="font-medium">{d.name || "Anonymous"}</span>
                    {d.email && (
                      <button onClick={() => copy(d.email)} className="text-imi-text/50 text-sm ml-2 hover:text-imi-gold transition-colors" title="Click to copy">
                        {d.email}
                      </button>
                    )}
                  </div>
                  <div className="text-imi-text/40 text-xs uppercase tracking-wider mt-1">
                    {d.tier || "custom"} · {new Date(d.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="flex items-center gap-2 md:gap-3">
                  <span className="text-[10px] uppercase tracking-[0.15em] text-green-400 border border-green-500/40 px-2 py-0.5">Paid</span>
                  {wasSent ? (
                    <button
                      onClick={() => resendThanks(d.session_id)}
                      disabled={isSending || !d.email}
                      data-testid={`resend-thanks-${d.session_id}`}
                      className="text-[10px] uppercase tracking-[0.15em] text-imi-gold border border-imi-gold/30 px-2 py-0.5 hover:bg-imi-gold/10 transition-colors disabled:opacity-40"
                      title={`Thanked ${new Date(d.thank_you_sent_at).toLocaleString()}. Click to send again.`}
                    >
                      {isSending ? "Sending…" : "Thanked · Resend"}
                    </button>
                  ) : (
                    <button
                      onClick={() => resendThanks(d.session_id)}
                      disabled={isSending || !d.email}
                      data-testid={`send-thanks-${d.session_id}`}
                      className="text-[10px] uppercase tracking-[0.15em] text-imi-bg bg-imi-gold hover:bg-imi-goldlight px-2 py-0.5 transition-colors disabled:opacity-40"
                      title={d.email ? "Send Harold's heartfelt thank-you email" : "No donor email on file"}
                    >
                      {isSending ? "Sending…" : "Send Thanks"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {paid.length === 0 && <div className="p-8 text-center text-imi-text/40" data-testid="no-donations">No donations yet.</div>}
        </div>
      </div>

      <div>
        <h3 className="font-serif text-xl text-imi-goldlight mb-4">Newsletter Subscribers</h3>
        <div className="border border-imi-border">
          {subs.map((s) => (
            <div key={s.id} className="flex items-center gap-4 px-4 py-3 border-b border-imi-border last:border-b-0">
              <span className="flex-1 text-imi-text truncate">{s.email}</span>
              <span className="text-imi-text/40 text-xs">{new Date(s.created_at).toLocaleDateString()}</span>
              <button onClick={() => delSub(s.id)} className="text-imi-text/40 hover:text-red-400"><Trash2 size={14} /></button>
            </div>
          ))}
          {subs.length === 0 && <div className="p-8 text-center text-imi-text/40">No subscribers yet.</div>}
        </div>
      </div>

      <div>
        <h3 className="font-serif text-xl text-imi-goldlight mb-4">Screening Requests</h3>
        <div className="border border-imi-border">
          {screenings.map((r) => (
            <div key={r.id} className="px-4 py-4 border-b border-imi-border last:border-b-0" data-testid={`screening-request-${r.id}`}>
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="font-serif text-imi-goldlight text-lg">{r.name}</span>
                    {r.organization && <span className="text-imi-text/60 text-sm">· {r.organization}</span>}
                    {r.city && <span className="text-imi-text/40 text-xs">· {r.city}</span>}
                  </div>
                  <div className="text-imi-text/60 text-sm mt-1">
                    <a href={`mailto:${r.email}`} className="text-imi-gold hover:underline">{r.email}</a>
                    {r.audience_size && <span className="ml-3 text-imi-text/40">· Audience: {r.audience_size}</span>}
                    {r.film && <span className="ml-3 text-imi-text/40">· Film: {r.film}</span>}
                  </div>
                  {r.notes && <p className="text-imi-text/70 text-sm mt-2 whitespace-pre-line">{r.notes}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-imi-text/40 text-xs">{new Date(r.created_at).toLocaleDateString()}</span>
                  <button onClick={() => delScreening(r.id)} className="text-imi-text/40 hover:text-red-400"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
          {screenings.length === 0 && <div className="p-8 text-center text-imi-text/40">No screening requests yet.</div>}
        </div>
      </div>

      <div>
        <h3 className="font-serif text-xl text-imi-goldlight mb-4 flex items-center gap-3">
          Family Story Submissions
          {stories.filter((st) => st.status === "new").length > 0 && (
            <span className="text-[10px] uppercase tracking-[0.2em] bg-imi-gold text-imi-bg px-2 py-1">{stories.filter((st) => st.status === "new").length} new</span>
          )}
        </h3>
        <div className="border border-imi-border">
          {stories.map((r) => (
            <div key={r.id} className="px-4 py-4 border-b border-imi-border last:border-b-0" data-testid={`family-story-${r.id}`}>
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="font-serif text-imi-goldlight text-lg">{r.loved_one_name}</span>
                    {r.facility && <span className="text-imi-text/60 text-sm">· {r.facility}</span>}
                    {r.time_in_solitary && <span className="text-imi-text/40 text-xs">· {r.time_in_solitary}</span>}
                    {r.anonymous && <span className="text-[10px] uppercase tracking-[0.2em] text-red-400 border border-red-400/40 px-2 py-0.5">⚠️ Anonymous</span>}
                    <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-imi-gold border border-imi-gold/30 px-2 py-0.5">{r.status || "new"}</span>
                  </div>
                  <div className="text-imi-text/60 text-sm mt-1">
                    From <span className="text-imi-goldlight">{r.submitter_name || (r.anonymous ? "Anonymous" : "—")}</span>
                    {r.relationship && <span> ({r.relationship})</span>}
                    · <a href={`mailto:${r.submitter_email}`} className="text-imi-gold hover:underline">{r.submitter_email}</a>
                    {r.submitter_phone && <span> · {r.submitter_phone}</span>}
                  </div>
                  <div className="mt-3 grid md:grid-cols-2 gap-3 text-sm">
                    {r.mental_health_history && <div><span className="text-imi-gold/70 text-xs uppercase tracking-wider">Mental health:</span> <span className="text-imi-text/70">{r.mental_health_history}</span></div>}
                    {r.current_condition && <div><span className="text-imi-gold/70 text-xs uppercase tracking-wider">Current condition:</span> <span className="text-imi-text/70">{r.current_condition}</span></div>}
                  </div>
                  {(r.prior_actions?.length || r.help_requested?.length) > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {r.prior_actions?.map((a) => <span key={`p-${a}`} className="text-[10px] uppercase tracking-wider bg-imi-elevated border border-imi-border px-2 py-1 text-imi-text/60">Prior: {a}</span>)}
                      {r.help_requested?.map((a) => <span key={`h-${a}`} className="text-[10px] uppercase tracking-wider bg-imi-gold/10 border border-imi-gold/30 px-2 py-1 text-imi-gold">Wants: {a}</span>)}
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-3 text-[10px] uppercase tracking-wider">
                    <span className={r.consent_permission ? "text-emerald-400" : "text-imi-text/30"}>{r.consent_permission ? "✓" : "✗"} Permission</span>
                    <span className={r.consent_followup ? "text-emerald-400" : "text-imi-text/30"}>{r.consent_followup ? "✓" : "✗"} Follow-up ok</span>
                    <span className={r.consent_public ? "text-emerald-400" : "text-imi-text/30"}>{r.consent_public ? "✓" : "✗"} Public feature ok</span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <span className="text-imi-text/40 text-xs">{new Date(r.created_at).toLocaleDateString()}</span>
                  <select value={r.status || "new"} onChange={(e) => setStoryStatus(r.id, e.target.value)} className="bg-imi-bg border border-imi-border text-xs px-2 py-1 text-imi-text" data-testid={`family-story-status-${r.id}`}>
                    <option value="new">New</option>
                    <option value="in_review">In review</option>
                    <option value="contacted">Contacted</option>
                    <option value="featured">Featured</option>
                    <option value="closed">Closed</option>
                  </select>
                  {r.consent_public && r.status !== "featured" && (
                    <button
                      onClick={() => promoteStory(r.id, r.loved_one_name)}
                      className="text-[10px] uppercase tracking-[0.15em] bg-imi-gold text-imi-bg px-2 py-1 hover:bg-imi-goldlight font-semibold"
                      data-testid={`family-story-promote-${r.id}`}
                      title="Add this person to the public 'Still Inside' grid"
                    >
                      ★ Promote
                    </button>
                  )}
                  <button onClick={() => delStory(r.id)} className="text-imi-text/40 hover:text-red-400" data-testid={`family-story-delete-${r.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
          {stories.length === 0 && <div className="p-8 text-center text-imi-text/40">No family stories submitted yet.</div>}
        </div>
      </div>
    </div>
  );
}

const SGroup = ({ title, children }) => (
  <div className="border border-imi-border bg-imi-surface p-6 md:p-7 space-y-5">
    <h3 className="font-serif text-xl text-imi-goldlight">{title}</h3>
    {children}
  </div>
);

const SField = ({ s, set, k, label, area, rows = 2, hint }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-2">{label}</label>
    {area ? (
      <textarea rows={rows} value={s[k] || ""} onChange={set(k)} data-testid={`set-${k}`}
        className="w-full bg-imi-bg border border-imi-border px-3 py-2.5 text-imi-text outline-none focus:border-imi-gold resize-none" />
    ) : (
      <input value={s[k] || ""} onChange={set(k)} data-testid={`set-${k}`}
        className="w-full bg-imi-bg border border-imi-border px-3 py-2.5 text-imi-text outline-none focus:border-imi-gold" />
    )}
    {hint && <p className="text-imi-text/30 text-xs mt-1">{hint}</p>}
  </div>
);

function SettingsTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const set = (k) => (e) => setS((v) => ({ ...v, [k]: e.target.value }));
  const setVal = (k, val) => setS((v) => ({ ...v, [k]: val }));
  const save = async () => {
    setSaving(true);
    try { await api.put("/settings", s); toast.success("Settings saved — changes are live"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const EMPH = "Tip: wrap words in *asterisks* to highlight them in gold.";

  // Manifesto chapter helpers
  const chapters = s.manifesto || [];
  const setChapter = (i, key, val) => {
    const next = chapters.map((c, idx) => idx === i ? { ...c, [key]: val } : c);
    setVal("manifesto", next);
  };
  const addChapter = () => setVal("manifesto", [...chapters, { n: String(chapters.length + 1).padStart(2, "0"), title: "", body: "" }]);
  const removeChapter = (i) => setVal("manifesto", chapters.filter((_, idx) => idx !== i));

  // Stats helpers
  const stats = s.stats || [];
  const setStat = (i, key, val) => setVal("stats", stats.map((st, idx) => idx === i ? { ...st, [key]: val } : st));
  const addStat = () => setVal("stats", [...stats, { value: "", label: "" }]);
  const removeStat = (i) => setVal("stats", stats.filter((_, idx) => idx !== i));

  // Press mentions helpers
  const press = s.press_mentions || [];
  const setPress = (i, key, val) => setVal("press_mentions", press.map((p, idx) => idx === i ? { ...p, [key]: val } : p));
  const addPress = () => setVal("press_mentions", [...press, { name: "", url: "" }]);
  const removePress = (i) => setVal("press_mentions", press.filter((_, idx) => idx !== i));

  // Ambassadors helpers
  const ambs = s.ambassadors || [];
  const setAmb = (i, key, val) => setVal("ambassadors", ambs.map((a, idx) => idx === i ? { ...a, [key]: val } : a));
  const addAmb = () => setVal("ambassadors", [...ambs, { name: "", role: "", city: "", bio: "", photo: "", link: "" }]);
  const removeAmb = (i) => setVal("ambassadors", ambs.filter((_, idx) => idx !== i));

  // Still Inside helpers
  const inside = s.still_inside || [];
  const setInside = (i, key, val) => setVal("still_inside", inside.map((p, idx) => idx === i ? { ...p, [key]: val } : p));
  const addInside = () => setVal("still_inside", [...inside, { name: "", facility: "", since: "", story: "", photo: "", write_address: "", petition_url: "", contact_script: "" }]);
  const removeInside = (i) => setVal("still_inside", inside.filter((_, idx) => idx !== i));

  // 6×9 Cell line helpers — each line is a plain string, in order
  const cellLines = s.cell_lines || [];
  const setCellLine = (i, val) => setVal("cell_lines", cellLines.map((l, idx) => idx === i ? val : l));
  const addCellLine = () => setVal("cell_lines", [...cellLines, ""]);
  const removeCellLine = (i) => setVal("cell_lines", cellLines.filter((_, idx) => idx !== i));
  const moveCellLine = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= cellLines.length) return;
    const next = [...cellLines];
    [next[i], next[j]] = [next[j], next[i]];
    setVal("cell_lines", next);
  };

  return (
    <div className="max-w-3xl space-y-6 pb-24">
      <SGroup title="Hero">
        <SField s={s} set={set} k="hero_kicker" label="Kicker" />
        <SField s={s} set={set} k="hero_sub" label="Subtitle" area />
        <Dropzone label="Hero background image" value={s.hero_image} accept="image/*" onUploaded={(id) => setVal("hero_image", id)} />
        <SField s={s} set={set} k="hero_video_url" label="Hero background video URL (YouTube or Vimeo — plays muted, on loop, behind the text)" hint="Leave empty to use the image. When set, the video plays as a subtle background layer while your Inside of Mental Injustice text remains on top." />
      </SGroup>

      <SGroup title="Manifesto">
        <SField s={s} set={set} k="manifesto_kicker" label="Kicker" />
        <SField s={s} set={set} k="manifesto_title" label="Title" hint={EMPH} />
        <div className="space-y-4">
          {chapters.map((c, i) => (
            <div key={i} className="border border-imi-border p-4 space-y-3 relative" data-testid={`manifesto-chapter-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-[0.2em] text-imi-gold">Chapter {i + 1}</span>
                <button onClick={() => removeChapter(i)} className="text-imi-text/40 hover:text-red-400" data-testid={`remove-chapter-${i}`}><Trash2 size={14} /></button>
              </div>
              <input value={c.n || ""} onChange={(e) => setChapter(i, "n", e.target.value)} placeholder="No. (01)" className="w-24 bg-imi-bg border border-imi-border px-3 py-2 text-imi-text outline-none focus:border-imi-gold" />
              <input value={c.title || ""} onChange={(e) => setChapter(i, "title", e.target.value)} placeholder="Title" className="w-full bg-imi-bg border border-imi-border px-3 py-2 text-imi-text outline-none focus:border-imi-gold" />
              <textarea rows={3} value={c.body || ""} onChange={(e) => setChapter(i, "body", e.target.value)} placeholder="Body" className="w-full bg-imi-bg border border-imi-border px-3 py-2 text-imi-text outline-none focus:border-imi-gold resize-none" />
            </div>
          ))}
          <button onClick={addChapter} data-testid="add-chapter" className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.15em] text-imi-gold border border-imi-gold/40 px-4 py-2 hover:bg-imi-gold/10"><Plus size={14} /> Add chapter</button>
        </div>
      </SGroup>

      <SGroup title="Press & Media — 'As seen in' strip">
        <div className="text-imi-text/50 text-xs -mt-2 mb-2">Outlets that have covered your work — appears as a trust band directly under the hero. Add each outlet by name; the URL is optional (makes the name clickable).</div>
        <SField s={s} set={set} k="press_kicker" label="Kicker text (e.g. 'As seen in', 'Featured in', 'Press')" />
        <div className="space-y-3 mt-2">
          {press.map((p, i) => (
            <div key={i} className="border border-imi-border p-4 space-y-2 relative" data-testid={`press-mention-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-[0.2em] text-imi-gold">Outlet {i + 1}</span>
                <button onClick={() => removePress(i)} className="text-imi-text/40 hover:text-red-400" data-testid={`remove-press-${i}`}><Trash2 size={14} /></button>
              </div>
              <input value={p.name || ""} onChange={(e) => setPress(i, "name", e.target.value)} placeholder="Outlet name (e.g. The Washington Post)" className="w-full bg-imi-bg border border-imi-border px-3 py-2 text-imi-text outline-none focus:border-imi-gold" data-testid={`press-name-${i}`} />
              <input value={p.url || ""} onChange={(e) => setPress(i, "url", e.target.value)} placeholder="URL to the coverage (optional)" className="w-full bg-imi-bg border border-imi-border px-3 py-2 text-imi-text outline-none focus:border-imi-gold" data-testid={`press-url-${i}`} />
              <Dropzone label={`${p.name || "Outlet"} logo (optional — PNG/SVG on transparent background)`} value={p.logo} accept="image/*" onUploaded={(id) => setPress(i, "logo", id)} />
            </div>
          ))}
          <button onClick={addPress} data-testid="add-press" className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.15em] text-imi-gold border border-imi-gold/40 px-4 py-2 hover:bg-imi-gold/10"><Plus size={14} /> Add outlet</button>
        </div>
      </SGroup>

      <SGroup title="Payments (PayPal / Venmo)">
        <div className="text-imi-text/50 text-xs -mt-2 mb-2">Enter your usernames only (no @, $, or URL). Each button appears on the site only when its handle is filled in and enabled.</div>
        <SField s={s} set={set} k="paypal_handle" label="PayPal.me handle (e.g. hcunningham1970)" hint="Fee-free personal PayPal — donors can select Friends & Family to send 100%" />
        <label className="flex items-center gap-3 mt-1 mb-3 text-imi-text/70 text-sm">
          <input type="checkbox" checked={s.paypal_enabled !== false} onChange={(e) => set("paypal_enabled", e.target.checked)} data-testid="paypal-enabled" className="accent-imi-gold" />
          Show PayPal button on the site
        </label>
        <div className="mt-4 p-4 border border-imi-border bg-imi-bg/40">
          <div className="text-[11px] uppercase tracking-[0.2em] text-imi-gold mb-1">Advanced — PayPal Business Hosted Button</div>
          <div className="text-imi-text/50 text-xs mb-3">
            Only fill these if you use a PayPal Business account and want the polished PayPal + Venmo + Card checkout embedded on the site.
            <span className="text-imi-gold"> Business charges ~2.9% + $0.49 per donation.</span> Leave BOTH fields empty to use the fee-free PayPal.me handle above.
          </div>
          <SField s={s} set={set} k="paypal_client_id" label="PayPal Business Client ID (leave EMPTY for fee-free personal PayPal)" />
          <SField s={s} set={set} k="paypal_hosted_button_id" label="PayPal Hosted Button ID (leave EMPTY for fee-free personal PayPal)" />
        </div>
        <SField s={s} set={set} k="cashapp_handle" label="Cash App $cashtag (saved for later)" hint="Cash App is temporarily hidden from the donation section" />
        <SField s={s} set={set} k="venmo_handle" label="Venmo username (e.g. HaroldCunningham)" />
        <label className="flex items-center gap-3 mt-1 text-imi-text/70 text-sm">
          <input type="checkbox" checked={s.venmo_enabled !== false} onChange={(e) => set("venmo_enabled", e.target.checked)} data-testid="venmo-enabled" className="accent-imi-gold" />
          Show Venmo button on the site
        </label>
      </SGroup>

      <SGroup title="6×9 Cell (Solitary section)">
        <div className="text-imi-text/50 text-xs -mt-2 mb-2">The gold-outlined cell footprint on the home page. Leave lines empty to restore the default stanza.</div>
        <SField s={s} set={set} k="cell_kicker" label="Kicker" />
        <SField s={s} set={set} k="cell_title" label="Title" hint={EMPH} />
        <SField s={s} set={set} k="cell_intro" label="Intro paragraph" area rows={3} />
        <Dropzone label="Cell background image (optional — appears as a haunting ghosted image inside the gold cell outline)" value={s.cell_image} accept="image/*" onUploaded={(id) => setVal("cell_image", id)} />
        <div>
          <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-gold mb-2">Lines inside the cell</label>
          <div className="text-imi-text/40 text-xs mb-3">One line per row. Drag to reorder is not enabled — use up/down. The first line and the two before-last lines render largest.</div>
          <div className="space-y-2">
            {(cellLines).map((line, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`cell-line-${i}`}>
                <span className="text-imi-text/30 text-xs w-6">{i + 1}.</span>
                <input
                  value={line || ""}
                  onChange={(e) => setCellLine(i, e.target.value)}
                  placeholder={i === 0 ? "Six feet by nine feet." : i === 1 ? "Twenty years." : "…"}
                  className="flex-1 bg-imi-bg border border-imi-border px-3 py-2 text-imi-text outline-none focus:border-imi-gold"
                />
                <button onClick={() => moveCellLine(i, -1)} disabled={i === 0} className="text-imi-text/40 hover:text-imi-gold disabled:opacity-30" title="Move up"><ChevronUp size={14} /></button>
                <button onClick={() => moveCellLine(i, 1)} disabled={i === cellLines.length - 1} className="text-imi-text/40 hover:text-imi-gold disabled:opacity-30" title="Move down"><ChevronDown size={14} /></button>
                <button onClick={() => removeCellLine(i)} className="text-imi-text/40 hover:text-red-400" title="Remove"><Trash2 size={14} /></button>
              </div>
            ))}
            <button onClick={addCellLine} data-testid="add-cell-line" className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.15em] text-imi-gold border border-imi-gold/40 px-4 py-2 hover:bg-imi-gold/10"><Plus size={14} /> Add line</button>
          </div>
        </div>
      </SGroup>

      <SGroup title="Goals / Stats">
        <div className="space-y-3">
          {stats.map((st, i) => (
            <div key={i} className="flex items-center gap-3" data-testid={`stat-${i}`}>
              <input value={st.value || ""} onChange={(e) => setStat(i, "value", e.target.value)} placeholder="12" className="w-28 bg-imi-bg border border-imi-border px-3 py-2 text-imi-text outline-none focus:border-imi-gold" />
              <input value={st.label || ""} onChange={(e) => setStat(i, "label", e.target.value)} placeholder="Films Produced" className="flex-1 bg-imi-bg border border-imi-border px-3 py-2 text-imi-text outline-none focus:border-imi-gold" />
              <button onClick={() => removeStat(i)} className="text-imi-text/40 hover:text-red-400"><Trash2 size={14} /></button>
            </div>
          ))}
          <button onClick={addStat} data-testid="add-stat" className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.15em] text-imi-gold border border-imi-gold/40 px-4 py-2 hover:bg-imi-gold/10"><Plus size={14} /> Add stat</button>
        </div>
      </SGroup>

      <SGroup title="Section Headings">
        <div className="grid sm:grid-cols-2 gap-5">
          <SField s={s} set={set} k="productions_kicker" label="Productions kicker" />
          <SField s={s} set={set} k="productions_title" label="Productions title" hint={EMPH} />
          <SField s={s} set={set} k="films_kicker" label="Films kicker" />
          <SField s={s} set={set} k="films_title" label="Films title" hint={EMPH} />
          <SField s={s} set={set} k="gallery_kicker" label="Gallery kicker" />
          <SField s={s} set={set} k="gallery_title" label="Gallery title" hint={EMPH} />
          <SField s={s} set={set} k="journal_kicker" label="Journal kicker" />
          <SField s={s} set={set} k="journal_title" label="Journal title" hint={EMPH} />
          <SField s={s} set={set} k="voices_kicker" label="Voices kicker" />
          <SField s={s} set={set} k="voices_title" label="Voices title" hint={EMPH} />
        </div>
      </SGroup>

      <SGroup title="About IMI">
        <SField s={s} set={set} k="about_kicker" label="Kicker" />
        <SField s={s} set={set} k="about_title" label="Title" area rows={2} />
        <SField s={s} set={set} k="about_body" label="Body" area rows={4} />
        <SField s={s} set={set} k="mission" label="Mission statement" area rows={4} />
        <Dropzone label="About image" value={s.about_image} accept="image/*" onUploaded={(id) => setVal("about_image", id)} />
      </SGroup>

      <SGroup title="Founder">
        <SField s={s} set={set} k="founder_name" label="Name" />
        <SField s={s} set={set} k="founder_role" label="Role" />
        <SField s={s} set={set} k="founder_bio" label="Short bio (shown in Voices card & About)" area rows={6} />
        <SField s={s} set={set} k="founder_biography_full" label="Full biography (shown on /harold page — use ## Section Title on its own line to split into chapters, **bold** for emphasis)" area rows={16} />
        <Dropzone label="Founder photo" value={s.founder_image} accept="image/*" onUploaded={(id) => setVal("founder_image", id)} />
      </SGroup>

      <SGroup title="Campaign pamphlet — Re-elect Harold (beside Our Mission)">
        <label className="flex items-center gap-3 text-imi-text/70 text-sm">
          <input type="checkbox" checked={s.campaign_enabled !== false} onChange={(e) => setVal("campaign_enabled", e.target.checked)} data-testid="campaign-enabled" className="accent-imi-gold" />
          Show the campaign card on the home page
        </label>
        <SField s={s} set={set} k="campaign_kicker" label="Small label above the slogan" />
        <SField s={s} set={set} k="campaign_slogan" label="Slogan (on top of the picture)" hint={EMPH} />
        <SField s={s} set={set} k="campaign_caption" label="Caption on the picture" />
        <SField s={s} set={set} k="campaign_link" label="Link when the pamphlet is clicked (optional — e.g. campaign page or PDF)" />
        <Dropzone label="Pamphlet image (leave empty to use the built-in Re-elect pamphlet)" value={s.campaign_image} accept="image/*" onUploaded={(id) => setVal("campaign_image", id)} />
      </SGroup>

      <SGroup title="The Full Story band — “From ADX solitary to elected office”">
        <div className="text-imi-text/50 text-xs -mt-2 mb-2">The callout on the home page that links to Harold's full biography (/harold).</div>
        <SField s={s} set={set} k="biography_kicker" label="Kicker" />
        <SField s={s} set={set} k="biography_title" label="Headline" hint={EMPH} />
        <SField s={s} set={set} k="biography_text" label="Message" area rows={5} />
        <SField s={s} set={set} k="biography_button" label="Button label" />
        <Dropzone label="Image (leave empty to use the podium photo with the flags)" value={s.biography_image} accept="image/*" onUploaded={(id) => setVal("biography_image", id)} />
        {s.biography_image && (
          <button type="button" onClick={() => setVal("biography_image", "")} data-testid="clear-biography-image" className="text-[11px] uppercase tracking-[0.15em] text-imi-text/50 hover:text-red-400">Remove image · use default podium photo</button>
        )}
      </SGroup>

      <SGroup title="Voices of the Movement — Ambassadors">
        <div className="text-imi-text/50 text-xs -mt-2 mb-2">Formerly incarcerated leaders, advocates, and storytellers working with IMI. Renders as a card grid on the homepage. Add as many as you like.</div>
        <SField s={s} set={set} k="ambassadors_kicker" label="Kicker (e.g. 'Voices of the Movement')" />
        <SField s={s} set={set} k="ambassadors_title" label="Section title" hint={EMPH} />
        <SField s={s} set={set} k="ambassadors_subtitle" label="Subtitle / description" area rows={2} />
        <div className="space-y-4 mt-2">
          {ambs.map((a, i) => (
            <div key={i} className="border border-imi-border p-4 space-y-3 relative" data-testid={`ambassador-editor-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-[0.2em] text-imi-gold">Ambassador {i + 1}</span>
                <button onClick={() => removeAmb(i)} className="text-imi-text/40 hover:text-red-400" data-testid={`remove-ambassador-${i}`}><Trash2 size={14} /></button>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <input value={a.name || ""} onChange={(e) => setAmb(i, "name", e.target.value)} placeholder="Full name" className="bg-imi-bg border border-imi-border px-3 py-2 text-imi-text outline-none focus:border-imi-gold" data-testid={`ambassador-name-${i}`} />
                <input value={a.role || ""} onChange={(e) => setAmb(i, "role", e.target.value)} placeholder="Role / title (e.g. Reform Advocate)" className="bg-imi-bg border border-imi-border px-3 py-2 text-imi-text outline-none focus:border-imi-gold" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <input value={a.city || ""} onChange={(e) => setAmb(i, "city", e.target.value)} placeholder="City, State" className="bg-imi-bg border border-imi-border px-3 py-2 text-imi-text outline-none focus:border-imi-gold" />
                <input value={a.link || ""} onChange={(e) => setAmb(i, "link", e.target.value)} placeholder="Website / social link (optional)" className="bg-imi-bg border border-imi-border px-3 py-2 text-imi-text outline-none focus:border-imi-gold" />
              </div>
              <textarea rows={3} value={a.bio || ""} onChange={(e) => setAmb(i, "bio", e.target.value)} placeholder="Short bio — 1 to 3 sentences about their story or mission." className="w-full bg-imi-bg border border-imi-border px-3 py-2 text-imi-text outline-none focus:border-imi-gold resize-y" />
              <Dropzone label={`${a.name || "Ambassador"} headshot`} value={a.photo} accept="image/*" onUploaded={(id) => setAmb(i, "photo", id)} />
            </div>
          ))}
          <button onClick={addAmb} data-testid="add-ambassador" className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.15em] text-imi-gold border border-imi-gold/40 px-4 py-2 hover:bg-imi-gold/10"><Plus size={14} /> Add ambassador</button>
        </div>
      </SGroup>

      <SGroup title="Still Inside — people currently in solitary confinement">
        <div className="text-imi-text/50 text-xs -mt-2 mb-2">Highlight individuals currently held in solitary confinement. Each card shows a live days-counter that auto-updates daily, and the section shows a "Free Them" tally summing days across everyone featured.</div>
        <SField s={s} set={set} k="still_inside_kicker" label="Kicker (e.g. 'Still Inside')" />
        <SField s={s} set={set} k="still_inside_title" label="Section title" hint={EMPH} />
        <SField s={s} set={set} k="still_inside_subtitle" label="Subtitle / description" area rows={2} />
        <div className="space-y-4 mt-2">
          {inside.map((p, i) => (
            <div key={i} className="border border-imi-border p-4 space-y-3 relative" data-testid={`still-inside-editor-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-[0.2em] text-imi-gold">Person {i + 1}</span>
                <button onClick={() => removeInside(i)} className="text-imi-text/40 hover:text-red-400" data-testid={`remove-still-inside-${i}`}><Trash2 size={14} /></button>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <input value={p.name || ""} onChange={(e) => setInside(i, "name", e.target.value)} placeholder="Full name" className="bg-imi-bg border border-imi-border px-3 py-2 text-imi-text outline-none focus:border-imi-gold" data-testid={`still-inside-name-${i}`} />
                <input value={p.facility || ""} onChange={(e) => setInside(i, "facility", e.target.value)} placeholder="Facility (e.g. ADX Florence)" className="bg-imi-bg border border-imi-border px-3 py-2 text-imi-text outline-none focus:border-imi-gold" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-imi-text/50 mb-1">Isolated since (date the counter starts from)</label>
                <input type="date" value={p.since || ""} onChange={(e) => setInside(i, "since", e.target.value)} className="bg-imi-bg border border-imi-border px-3 py-2 text-imi-text outline-none focus:border-imi-gold" data-testid={`still-inside-since-${i}`} />
              </div>
              <textarea rows={3} value={p.story || ""} onChange={(e) => setInside(i, "story", e.target.value)} placeholder="Their story — 2 to 4 sentences. Background, why they're in solitary, mental health situation, etc." className="w-full bg-imi-bg border border-imi-border px-3 py-2 text-imi-text outline-none focus:border-imi-gold resize-y" />
              <Dropzone label={`${p.name || "Person"} photo`} value={p.photo} accept="image/*" onUploaded={(id) => setInside(i, "photo", id)} />
              <div className="border-t border-imi-border pt-3 space-y-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-imi-gold">Take-action fields (optional)</div>
                <textarea rows={2} value={p.write_address || ""} onChange={(e) => setInside(i, "write_address", e.target.value)} placeholder="Mailing address (for supporters to write letters, incl. inmate ID if required)" className="w-full bg-imi-bg border border-imi-border px-3 py-2 text-imi-text outline-none focus:border-imi-gold resize-y" />
                <input value={p.petition_url || ""} onChange={(e) => setInside(i, "petition_url", e.target.value)} placeholder="Petition URL (e.g. change.org link)" className="w-full bg-imi-bg border border-imi-border px-3 py-2 text-imi-text outline-none focus:border-imi-gold" />
                <textarea rows={2} value={p.contact_script || ""} onChange={(e) => setInside(i, "contact_script", e.target.value)} placeholder="Contact-officials script — a short email supporters can send to warden, senator, etc. Pre-fills their email client." className="w-full bg-imi-bg border border-imi-border px-3 py-2 text-imi-text outline-none focus:border-imi-gold resize-y" />
              </div>
            </div>
          ))}
          <button onClick={addInside} data-testid="add-still-inside" className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.15em] text-imi-gold border border-imi-gold/40 px-4 py-2 hover:bg-imi-gold/10"><Plus size={14} /> Add person</button>
        </div>
      </SGroup>

      <SGroup title="Marquee & Contact">
        <SField s={s} set={set} k="marquee_text" label="Marquee (• separated)" />
        <SField s={s} set={set} k="contact_email" label="Contact email" />
      </SGroup>

      <div className="sticky bottom-4 z-10">
        <button onClick={save} disabled={saving} data-testid="settings-save" className="w-full bg-imi-gold text-imi-bg px-6 py-4 text-sm uppercase tracking-[0.15em] font-semibold hover:bg-imi-goldlight transition-colors disabled:opacity-60 shadow-lg shadow-black/40">
          {saving ? "Saving…" : "Save all changes"}
        </button>
      </div>
    </div>
  );
}

function ManifestoTab() {
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/manifesto/signatures/admin");
      setData(data);
    } catch {
      toast.error("Could not load signatures.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!confirm("Remove this signature from the wall?")) return;
    try {
      await api.delete(`/manifesto/signatures/${id}`);
      toast.success("Signature removed.");
      load();
    } catch { toast.error("Could not delete."); }
  };

  const [bulk, setBulk] = useState("");
  const [importing, setImporting] = useState(false);
  const importPetition = async () => {
    if (!confirm("Add every name from the 'Petition for Human Dignity' to the wall? Names already imported are skipped.")) return;
    setImporting(true);
    try {
      const { data } = await api.post("/manifesto/signatures/import-petition");
      toast.success(`Petition imported — ${data.added} names added${data.skipped ? `, ${data.skipped} already on the wall` : ""}.`);
      load();
    } catch { toast.error("Import failed."); }
    finally { setImporting(false); }
  };
  const addBulk = async () => {
    const names = bulk.split(/\n|,/).map((n) => n.trim()).filter(Boolean);
    if (!names.length) { toast.error("Paste at least one name."); return; }
    setImporting(true);
    try {
      const { data } = await api.post("/manifesto/signatures/bulk", { names });
      toast.success(`${data.added} names added${data.skipped ? `, ${data.skipped} duplicates skipped` : ""}.`);
      setBulk("");
      load();
    } catch { toast.error("Could not add names."); }
    finally { setImporting(false); }
  };

  const q = filter.trim().toLowerCase();
  const list = q
    ? data.items.filter((s) =>
        (s.name || "").toLowerCase().includes(q) ||
        (s.location || "").toLowerCase().includes(q) ||
        (s.message || "").toLowerCase().includes(q)
      )
    : data.items;

  return (
    <div className="space-y-5">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-imi-text/60 text-sm">Signatures on the Manifesto Wall</div>
          <div className="mt-1 font-serif text-3xl text-imi-goldlight" data-testid="admin-sig-total">
            {data.total} <span className="text-imi-text/40 text-lg">total</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="admin-sig-search"
            placeholder="Search name, city or note…"
            className="bg-transparent border border-imi-border focus:border-imi-gold outline-none px-3 py-2 text-sm text-imi-text placeholder:text-imi-text/40 min-w-[280px]"
          />
          <button onClick={load} data-testid="admin-sig-refresh" className="text-imi-text/50 hover:text-imi-gold text-xs uppercase tracking-[0.2em] border border-imi-border hover:border-imi-gold px-3 py-2">Refresh</button>
        </div>
      </div>

      <div className="border border-imi-gold/25 bg-imi-surface p-5 grid md:grid-cols-2 gap-5" data-testid="admin-sig-bulk">
        <div>
          <div className="font-serif text-lg text-imi-goldlight">Petition for Human Dignity</div>
          <p className="mt-1 text-imi-text/50 text-xs leading-relaxed">Add every signer from the handwritten petition to the wall as individual names (ID numbers are never published). Safe to click more than once — names already on the wall are skipped.</p>
          <button onClick={importPetition} disabled={importing} data-testid="admin-sig-import-petition" className="mt-4 inline-flex items-center gap-2 bg-imi-gold text-imi-bg px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] font-semibold hover:bg-imi-goldlight disabled:opacity-60">
            {importing ? "Importing…" : "Import petition names"}
          </button>
        </div>
        <div>
          <div className="font-serif text-lg text-imi-goldlight">Add names in bulk</div>
          <textarea rows={4} value={bulk} onChange={(e) => setBulk(e.target.value)} data-testid="admin-sig-bulk-input" placeholder={"One name per line\nJane Doe\nJohn Smith"}
            className="mt-2 w-full bg-imi-bg border border-imi-border px-3 py-2 text-sm text-imi-text outline-none focus:border-imi-gold resize-none" />
          <button onClick={addBulk} disabled={importing} data-testid="admin-sig-bulk-submit" className="mt-2 inline-flex items-center gap-2 border border-imi-gold/50 text-imi-gold px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] hover:bg-imi-gold/10 disabled:opacity-60">
            Add to wall
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-imi-text/40 border border-imi-border">Loading…</div>
      ) : list.length === 0 ? (
        <div className="p-8 text-center text-imi-text/40 border border-imi-border" data-testid="admin-sig-empty">
          {data.total === 0 ? "No signatures yet." : "No signatures match your search."}
        </div>
      ) : (
        <div className="border border-imi-border divide-y divide-imi-border">
          {list.map((s) => (
            <div key={s.id} data-testid={`admin-sig-${s.id}`} className="grid md:grid-cols-12 gap-4 p-4 hover:bg-imi-elevated transition-colors items-start">
              <div className="md:col-span-3">
                <div className="text-imi-text font-medium flex items-center gap-2">
                  <Star size={13} className="text-imi-gold flex-shrink-0" />
                  <span className="truncate">{s.name}</span>
                </div>
                {s.location && (
                  <div className="mt-1 text-imi-text/50 text-xs flex items-center gap-1">
                    <MapPin size={11} /> {s.location}
                  </div>
                )}
              </div>
              <div className="md:col-span-6 text-imi-text/70 text-sm leading-relaxed">
                {s.message || <span className="text-imi-text/30">— no note —</span>}
              </div>
              <div className="md:col-span-2 text-imi-text/40 text-xs">
                {new Date(s.created_at).toLocaleString()}
                {s.ip && <div className="text-imi-text/25 mt-1">IP {s.ip}</div>}
              </div>
              <div className="md:col-span-1 md:text-right">
                <button
                  onClick={() => del(s.id)}
                  data-testid={`admin-sig-del-${s.id}`}
                  className="text-imi-text/40 hover:text-red-400 transition-colors"
                  title="Remove"
                ><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const [tab, setTab] = useState("content");

  if (ready && !user) return <Navigate to="/admin" replace />;
  if (!ready) return <div className="min-h-screen bg-imi-bg" />;

  const NAV = [
    { id: "content", label: "Content", icon: LayoutGrid },
    { id: "stories", label: "Stories", icon: MessageSquare },
    { id: "manifesto", label: "Manifesto", icon: Feather },
    { id: "support", label: "Support", icon: DollarSign },
    { id: "messages", label: "Messages", icon: Mail },
    { id: "settings", label: "Settings", icon: SettingsIcon },
  ];

  return (
    <div className="min-h-screen bg-imi-bg flex">
      <aside className="w-60 border-r border-imi-border bg-imi-surface flex flex-col sticky top-0 h-screen">
        <div className="p-6 border-b border-imi-border flex items-center gap-3">
          <img src="/imi-logo.jpg" alt="IMI" className="h-9 w-9 object-cover rounded-sm border border-imi-border" />
          <span className="font-serif text-imi-goldlight">IMI Admin</span>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => setTab(n.id)} data-testid={`nav-${n.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${tab === n.id ? "bg-imi-gold/10 text-imi-gold border-l-2 border-imi-gold" : "text-imi-text/60 hover:text-imi-text border-l-2 border-transparent"}`}>
              <n.icon size={16} /> {n.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-imi-border space-y-2">
          <Link to="/" target="_blank" className="flex items-center gap-3 px-4 py-2 text-sm text-imi-text/60 hover:text-imi-gold"><ExternalLink size={16} /> View site</Link>
          <button onClick={logout} data-testid="logout-btn" className="w-full flex items-center gap-3 px-4 py-2 text-sm text-imi-text/60 hover:text-red-400"><LogOut size={16} /> Logout</button>
        </div>
      </aside>
      <main className="flex-1 p-8 md:p-12 overflow-x-hidden">
        <h1 className="font-serif text-3xl text-imi-text mb-8 capitalize">{tab}</h1>
        <TabErrorBoundary resetKey={tab}>
          {tab === "content" && <ContentTab />}
          {tab === "stories" && <StoriesTab />}
          {tab === "manifesto" && <ManifestoTab />}
          {tab === "support" && <SupportTab />}
          {tab === "messages" && <MessagesTab />}
          {tab === "settings" && <SettingsTab />}
        </TabErrorBoundary>
      </main>
    </div>
  );
}
